import { Expand, FastForward, Play, Rewind, Volume2, VolumeX } from "lucide-react";
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { ICON } from "../../ui/index.ts";

/**
 * A clip in the card, driven the way a phone drives a clip now.
 *
 * The picture is the control. A tap stops and starts it, a press holds it still, and a press on
 * either side runs it that way — the gestures a thumb already knows from every feed it scrolls,
 * and none of them a row of buttons at a mouse's size across the bottom of a photograph.
 *
 * Two things a press cannot say keep a button of their own, in the corners where a thumb rests
 * and the picture is least likely to matter: sound, and the way out to full size. Full size is
 * the player with the scrubber on it, for when somebody wants a particular second rather than a
 * look at the clip.
 */

/** A press longer than this is a hold rather than a tap. */
const HOLD_MS = 180;
/** How fast a held side runs, either way. */
const EDGE_RATE = 2;
/** The share of the width at each side that runs the clip rather than holding it still. */
const EDGE = 0.25;
/** A frame, roughly. The first step back is worth this much, so it moves rather than standing still. */
const FRAME_MS = 1000 / 60;
/**
 * How long to wait for a seek to land before asking again.
 *
 * Long, because this is for a decoder that has died rather than one that is working. Measured on
 * the phone, a backward seek on a clip with keyframes eight seconds apart takes between 220 and
 * 440 milliseconds — the further from the keyframe, the longer — and every one of those lands a
 * frame if it is left alone. A quarter of a second of patience interrupted nearly all of them and
 * asked again, which is the frozen picture this was meant to fix, at a slower cadence.
 */
const PATIENCE_MS = 2000;
/** The furthest back one step may jump, so a slow seek slides on rather than leaping. */
const MAX_STEP_SEC = 0.25;

/** What a press is doing while it is down. */
type Hold = "still" | "forward" | "back";

/** Play, and swallow the refusal. A browser may decline, and nothing here depends on it. */
const start = (v: HTMLVideoElement | null) =>
  void Promise.resolve(v?.play()).catch(() => undefined);

export function VideoReel({
  src,
  poster,
  label,
  autoPlay,
  active,
  onFull,
}: {
  src: string;
  /** The still the card was already showing, so the first frame is not a black hole. */
  poster: string | null;
  /** What the recording is, for whoever cannot see it. */
  label: string;
  /** Start by itself. The caller decides — on a mobile network, nothing starts by itself. */
  autoPlay: boolean;
  /** This is the card being read. The one beside it is scenery and must not be heard. */
  active: boolean;
  /** Open the clip full size, where the player has a scrubber and the usual controls. */
  onFull: () => void;
}) {
  const media = useRef<HTMLVideoElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  // Muted to begin with, because a clip that starts itself must not start talking: a library
  // scrolled past in company is the normal way to meet one.
  const [muted, setMuted] = useState(true);
  const [hold, setHold] = useState<Hold | null>(null);
  /** How far through, 0 to 1, for the hairline along the foot. */
  const [through, setThrough] = useState(0);
  /** The finger that is down, if one is: a second is a pinch and gives up the press. */
  const press = useRef<{ id: number; timer: number; held: boolean; wasPlaying: boolean } | null>(
    null,
  );
  /** Stops whatever is walking the clip backwards, if anything is. */
  const back = useRef<(() => void) | null>(null);

  // A card swiped away is scenery: it stops where it is rather than playing on out of sight,
  // which is also the only way two cards are never heard at once.
  useEffect(() => {
    if (!active) media.current?.pause();
  }, [active]);

  // Started by hand rather than by the attribute. A video element keeps the right to autoplay
  // until something it counts as the reader pauses it, and winding back to the very start hands
  // that right back: the clip would begin playing again under the thumb that was rewinding it.
  useEffect(() => {
    if (autoPlay && active) start(media.current);
  }, [autoPlay, active]);

  // Nothing may outlive the card: a frame loop still seeking a video that has gone is a leak
  // the reader would see as a stutter on whatever opens next.
  useEffect(() => () => stopBack(), []);

  function stopBack() {
    back.current?.();
    back.current = null;
  }

  /**
   * Walk the clip backwards, because no video plays at a negative rate: it is paused, and seeked
   * back a frame at a time at the speed the other side runs forward.
   *
   * The seeks are paced by frames landing, not by the screen refreshing. Assigning `currentTime`
   * starts a seek and returns at once, and assigning it again abandons the one in flight. Asked
   * once an animation frame — every sixteen milliseconds, against a seek that takes tens — a
   * frame never finished decoding and the picture never changed, while `currentTime` read back
   * every time what had just been asked for: a clock running backwards over a still photograph.
   *
   * So the next frame is asked for when `seeked` says the last one arrived, and the clip runs
   * back as fast as the decoder can actually manage. Where that is slower than the clip plays the
   * picture is jerky — a long way between keyframes is a lot of decoding to reach one frame — but
   * it is the clip running backwards rather than standing still.
   */
  function runBack(v: HTMLVideoElement) {
    // A frame's worth of credit, so the first step moves: a seek to where the clip already is may
    // not be a seek at all, and would land no event to carry on from.
    let last = performance.now() - FRAME_MS;
    let patience = 0;
    const step = () => {
      window.clearTimeout(patience);
      // The start is as far back as it goes. The hold stands — the mark stays up, and letting go
      // still plays on — but there is nothing left to seek.
      if (v.currentTime <= 0) return;
      const now = performance.now();
      // However slowly the frames come, the clip runs back at the speed the other side runs
      // forward, so a press reads the same both ways. One step is capped, so a seek that took a
      // moment to land does not then leap half the clip.
      const by = Math.min((now - last) / 1000, MAX_STEP_SEC) * EDGE_RATE;
      last = now;
      // A decoder that does not answer is asked again. Waiting for ever on a frame that is not
      // coming would end the hold with the picture stopped, which is what this all began as.
      patience = window.setTimeout(step, PATIENCE_MS);
      v.currentTime = Math.max(0, v.currentTime - by);
    };
    v.addEventListener("seeked", step);
    back.current = () => {
      window.clearTimeout(patience);
      v.removeEventListener("seeked", step);
    };
    step();
  }

  /** Which part of the picture a press landed in. */
  function zoneOf(clientX: number): Hold {
    const box = stage.current?.getBoundingClientRect();
    if (!box || box.width === 0) return "still";
    const across = (clientX - box.x) / box.width;
    if (across < EDGE) return "back";
    if (across > 1 - EDGE) return "forward";
    return "still";
  }

  function toggle() {
    const v = media.current;
    if (!v) return;
    if (v.paused) start(v);
    else v.pause();
  }

  function beginHold(what: Hold) {
    const v = media.current;
    if (!v) return;
    setHold(what);
    if (what === "forward") {
      v.playbackRate = EDGE_RATE;
      start(v);
      return;
    }
    // Both of the others stand the clip still; running back then walks it the other way by hand,
    // a frame at a time.
    v.pause();
    if (what === "back") runBack(v);
  }

  /** Let go: back to its own speed, and playing again if it was playing when the finger landed. */
  function endHold(resume: boolean) {
    stopBack();
    setHold(null);
    const v = media.current;
    if (!v) return;
    v.playbackRate = 1;
    if (resume) start(v);
  }

  /** Give up whatever this press was doing without counting it as a tap. */
  function dropPress() {
    const p = press.current;
    if (!p) return;
    window.clearTimeout(p.timer);
    press.current = null;
    if (p.held) endHold(p.wasPlaying);
  }

  function onDown(e: ReactPointerEvent<HTMLDivElement>) {
    const v = media.current;
    if (!v) return;
    // A second finger on the picture is a pinch, never a press on the clip.
    if (press.current) return dropPress();
    const x = e.clientX;
    const wasPlaying = !v.paused;
    const timer = window.setTimeout(() => {
      if (!press.current) return;
      press.current.held = true;
      beginHold(zoneOf(x));
    }, HOLD_MS);
    press.current = { id: e.pointerId, timer, held: false, wasPlaying };
  }

  function onUp(e: ReactPointerEvent<HTMLDivElement>) {
    const p = press.current;
    if (!p || p.id !== e.pointerId) return;
    window.clearTimeout(p.timer);
    press.current = null;
    // Short is a tap, which starts and stops it; long was already doing something, and letting
    // go is the end of that rather than a second instruction.
    if (p.held) endHold(p.wasPlaying);
    else toggle();
  }

  function onCancel(e: ReactPointerEvent<HTMLDivElement>) {
    const p = press.current;
    if (!p || p.id !== e.pointerId) return;
    dropPress();
  }

  return (
    <div
      ref={stage}
      className="reel"
      data-hold={hold ?? undefined}
      onPointerDown={onDown}
      onPointerUp={onUp}
      onPointerCancel={onCancel}
      // A press on a picture is this app's own gesture. Left alone, the WebView reads it as a
      // long press on a video and opens its own menu over the top — which is what used to
      // interrupt a hold a few hundred milliseconds in.
      onContextMenu={(e) => e.preventDefault()}
    >
      <video
        ref={media}
        className="reel-media"
        src={src}
        {...(poster ? { poster } : {})}
        aria-label={label}
        muted={muted}
        playsInline
        loop
        preload={autoPlay ? "auto" : "metadata"}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => {
          const v = e.currentTarget;
          setThrough(v.duration > 0 ? v.currentTime / v.duration : 0);
        }}
      />

      {/* What the press is doing, while it is doing it. A clip running under a thumb looks like
          a clip playing, so the speed says which it is. */}
      {hold === "forward" && (
        <span className="reel-rate" aria-hidden="true">
          <FastForward size={14} strokeWidth={2} fill="currentColor" />
          {EDGE_RATE}×
        </span>
      )}
      {hold === "back" && (
        <span className="reel-rate" aria-hidden="true">
          <Rewind size={14} strokeWidth={2} fill="currentColor" />
          {EDGE_RATE}×
        </span>
      )}
      {/* Stopped is the one state a still picture cannot tell you on its own. */}
      {!playing && hold === null && (
        <span className="reel-stopped" aria-hidden="true">
          <Play size={24} strokeWidth={2} fill="currentColor" />
        </span>
      )}

      {/* How far through, and nothing to aim at: a second is found full size, not here. */}
      <span className="reel-through" aria-hidden="true">
        <span style={{ transform: `scaleX(${through})` }} />
      </span>

      <button
        type="button"
        className="reel-btn full"
        aria-label="Open this video full size"
        onClick={(e) => {
          e.stopPropagation();
          media.current?.pause();
          onFull();
        }}
      >
        <Expand {...ICON} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="reel-btn mute"
        aria-pressed={muted}
        aria-label={muted ? "Unmute" : "Mute"}
        onClick={(e) => {
          e.stopPropagation();
          setMuted((m) => !m);
        }}
      >
        {muted ? (
          <VolumeX {...ICON} aria-hidden="true" />
        ) : (
          <Volume2 {...ICON} aria-hidden="true" />
        )}
      </button>
    </div>
  );
}
